import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PreAuthNavbar } from '@/components/navigation/PreAuthNavbar';
import { Footer } from '@/components/marketing/Footer';
import { AuthCard } from '@/components/auth/AuthCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authService } from '@/services/authService';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

const forgotPasswordSchema = Yup.object({
    email: Yup.string()
        .email('Invalid email format')
        .required('Email is required'),
});

export default function ForgotPasswordPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: forgotPasswordSchema,
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                await authService.forgotPassword(values);
                setSubmittedEmail(values.email);
                setIsSubmitted(true);
                toast.success('Password reset email sent');
            } catch (error: any) {
                toast.error(error.message || 'Failed to send reset email');
            } finally {
                setIsLoading(false);
            }
        },
    });

    if (isSubmitted) {
        return (
            <div className="min-h-screen flex flex-col">
                <PreAuthNavbar />
                <main className="flex-1 flex items-center justify-center px-4 py-12">
                    <AuthCard
                        title="Check Your Email"
                        description="We've sent password reset instructions to your email address."
                    >
                        <div className="space-y-4">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full">
                                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                            </div>

                            <div className="text-center space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    We've sent a password reset link to:
                                </p>
                                <p className="font-medium">{submittedEmail}</p>
                            </div>

                            <div className="text-center space-y-3">
                                <p className="text-xs text-muted-foreground">
                                    Didn't receive the email? Check your spam folder or try again.
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setIsSubmitted(false);
                                        formik.resetForm();
                                    }}
                                >
                                    Try Different Email
                                </Button>
                            </div>

                            <div className="text-center pt-4">
                                <Link
                                    to="/login"
                                    className="text-sm text-primary hover:underline"
                                >
                                    ← Back to Login
                                </Link>
                            </div>
                        </div>
                    </AuthCard>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <PreAuthNavbar />
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <AuthCard
                    title="Reset Your Password"
                    description="Enter your email address and we'll send you a reset link."
                >
                    <form onSubmit={formik.handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Enter your email address"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.email && formik.errors.email ? 'border-destructive' : ''}
                                autoComplete="email"
                            />
                            {formik.touched.email && formik.errors.email && (
                                <p className="text-sm text-destructive">{formik.errors.email}</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading || !formik.isValid || !formik.dirty}
                            className="w-full"
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>

                        <div className="text-center space-y-2">
                            <Link
                                to="/login"
                                className="text-sm text-primary hover:underline"
                            >
                                ← Back to Login
                            </Link>
                            <p className="text-xs text-muted-foreground">
                                Remember your password? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
                            </p>
                        </div>
                    </form>
                </AuthCard>
            </main>
            <Footer />
        </div>
    );
}
